import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';

const SearchableInput = ({
  apiService, // 검색 API
  searchField, // 검색 API 쿼리 필드
  additionalParams = {}, // 추가 쿼리 파라미터
  displayField = 'name', // 선택된 아이템의 표시할 필드
  valueField = null,
  value = '',
  onChange,
  onSelect,
  onBlur,
  placeholder = 'Search...',
  renderItem,
  disabled = false,
  minSearchLength = 2,
  visibleItems = 5, // 결과로 보여질 아이템 수
  className = '',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const [searchTerm, setSearchTerm] = useState(value);
  const [results, setResults] = useState([]);
  const [itemHeight, setItemHeight] = useState(56);

  useEffect(() => {
    if (value !== searchTerm) {
      setSearchTerm(value);
    }
  }, [value, searchTerm]);

  const wrapperRef = useRef(null);
  const firstResultItemRef = useRef(null);

  useEffect(() => {
    if (firstResultItemRef?.current) {
      const height = firstResultItemRef.current.offsetHeight;

      setItemHeight(height);
    }
  }, [results]);

  // additionalParams의 잦은 레퍼런스 변경을 흡수
  const paramsRef = useRef(additionalParams);
  useEffect(() => {
    if (paramsRef.current !== additionalParams) {
      paramsRef.current = additionalParams;
    }
  }, [additionalParams]);

  const inFlightKeyRef = useRef(null);

  const searchAPI = useCallback(
    async term => {
      const trimmedTerm = term.trim();

      if (trimmedTerm.length < minSearchLength) {
        setResults([]);
        return;
      }
      const key = JSON.stringify({
        q: trimmedTerm,
        sf: searchField,
        p: paramsRef.current,
      });
      if (inFlightKeyRef.current === key) return;
      inFlightKeyRef.current = key;

      setIsLoading(true);
      setError(null);

      try {
        const response = await apiService.getList({
          [searchField]: trimmedTerm,
          ...paramsRef.current,
        });

        const searchResults = Array.isArray(response)
          ? response
          : response?.results || [];

        setResults(searchResults);
      } catch (err) {
        setError('Failed to fetch results');
        setResults([]);
      } finally {
        setIsLoading(false);
        inFlightKeyRef.current = null;
      }
    },
    [apiService, searchField, minSearchLength],
  );

  const debouncedSearch = useMemo(
    () => debounce(t => searchAPI(t), 300),
    [searchAPI], // apiService/searchField/minSearchLength 변경 때만 인스턴스 교체
  );

  const handleInputChange = e => {
    const term = e.target.value;

    setSearchTerm(term);
    onChange?.(term);
    debouncedSearch(term);
  };

  const handleItemSelect = useCallback(
    item => {
      if (valueField) {
        onSelect?.(item, item[valueField]);
      } else {
        onSelect?.(item);
      }
      setSearchTerm(item[displayField]);
      setShowResults(false);
    },
    [onSelect, displayField, valueField],
  );

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (searchTerm.length >= minSearchLength) {
      debouncedSearch(searchTerm);
    } else {
      setResults([]);
    }
  }, [searchTerm, minSearchLength, debouncedSearch]);

  return (
    <div ref={wrapperRef} className="relative">
      <Form.Control
        type="text"
        size="sm"
        value={searchTerm}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={() => setShowResults(true)}
        onBlur={onBlur}
        disabled={disabled}
        className={`mb-1 ${className || ''}`}
      />

      {showResults && (
        <div
          className="absolute w-full bg-white shadow-lg rounded-md mt-1 border overflow-auto z-50"
          style={{ maxHeight: `${visibleItems * itemHeight}px` }}
        >
          {isLoading ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : error ? (
            <div className="p-3 text-center text-red-500">{error}</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              {searchTerm.length < minSearchLength
                ? `Enter ${minSearchLength} or more characters`
                : 'No results found'}
            </div>
          ) : (
            results.map((item, index) => (
              <div
                key={item.id || index}
                ref={index === 0 ? firstResultItemRef : null}
                className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                onClick={() => handleItemSelect(item)}
              >
                {renderItem ? renderItem(item) : item[displayField]}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableInput;
